import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { storeService } from '../services/storeService';
import { StoreCategory, StoreProductDetail, StoreVariant, StoreVariantColor, PaymentMethod, PaymentMethodLabel } from '../models';
import { PaymentMethodIcon } from '../components/ui/PaymentMethodIcon';
import { useCartStore } from '../store/cartStore';
import { Spinner } from '../components/ui/Spinner';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { HeartIcon } from '../components/ui/HeartIcon';
import { AlertTriangleIcon, CheckIcon } from '../components/ui/Icons';
import { productPlaceholderIcon } from '../utils/productPlaceholder';
import { ProductReviews } from '../components/product/ProductReviews';
import { useFavoriteToggle } from '../hooks/useFavoriteToggle';
import { usePaymentSettings } from '../hooks/usePaymentSettings';
import { formatCurrency } from '../utils/currency';
import { safeImageUrl } from '../utils/url';
import { getPixPrice, getInstallmentInfo } from '../utils/pricing';
import { buildProductInquiryWhatsAppUrl } from '../utils/whatsapp';
import { WHATSAPP_NUMBER } from '../constants/contact';
import { AGE_VARIATION_TYPE, getVariationLabel } from '../utils/variantResolution';
import { PROMOTION_CAMPAIGN_ENABLED } from '../config/promotionCampaign';

function WhatsAppIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.75}
        d="M8 12a4 4 0 108 0 4 4 0 00-8 0zm9.5 5.5L21 21M4 12a8 8 0 1113.86 5.47L19 21l-3.66-1.14A8 8 0 014 12z"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeWidth={2.5} d="M12 5v14M5 12h14" />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeWidth={2.5} d="M5 12h14" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      className="w-4 h-4 text-text-medium transition-transform duration-200 group-open:rotate-180"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

const GENDER_LABELS: Record<number, string> = {
  1: 'Meninos',
  2: 'Meninas',
  3: 'Outros',
  4: 'Unissex',
};

interface AgeSizeGuideRow {
  age: string;
  height: string;
  weight: string;
}

const AGE_SIZE_GUIDE: AgeSizeGuideRow[] = [
  { age: '2 anos', height: '85 - 92 cm', weight: '12 - 14 kg' },
  { age: '4 anos', height: '98 - 104 cm', weight: '15 - 17 kg' },
  { age: '6 anos', height: '110 - 116 cm', weight: '19 - 22 kg' },
  { age: '8 anos', height: '122 - 128 cm', weight: '24 - 28 kg' },
  { age: '10 anos', height: '134 - 140 cm', weight: '29 - 34 kg' },
  { age: '12 anos', height: '146 - 152 cm', weight: '36 - 42 kg' },
  { age: '14 anos', height: '158 - 164 cm', weight: '45 - 52 kg' },
];

function extractUniqueColors(variants: StoreVariant[]): StoreVariantColor[] {
  const colors: StoreVariantColor[] = [];
  const seen = new Set<number>();
  for (const v of variants) {
    if (v.color && !seen.has(v.color.id)) {
      seen.add(v.color.id);
      colors.push(v.color);
    }
  }
  return colors;
}

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const addItem = useCartStore((s) => s.addItem);
  const { isFavorited, handleToggle } = useFavoriteToggle();
  const { settings: paymentSettings } = usePaymentSettings();

  const [product, setProduct] = useState<StoreProductDetail | null>(null);
  const [categories, setCategories] = useState<StoreCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [mainImage, setMainImage] = useState('');
  const [selectedColor, setSelectedColor] = useState<StoreVariantColor | null>(null);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const loadedIdRef = useRef<number | null>(null);

  const openLightbox = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setZoomLevel((z) => Math.min(z + 0.5, 4));
    setPanOffset({ x: 0, y: 0 });
  };

  const handleZoomOut = () => {
    setZoomLevel((z) => {
      const next = Math.max(z - 0.5, 1);
      if (next === 1) setPanOffset({ x: 0, y: 0 });
      return next;
    });
  };

  const handleLightboxPrev = useCallback(() => {
    if (!product || product.images.length <= 1) return;
    const currentIdx = product.images.findIndex((img) => img.imageUrl === mainImage);
    const prevIdx = currentIdx <= 0 ? product.images.length - 1 : currentIdx - 1;
    setMainImage(product.images[prevIdx].imageUrl);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  }, [product, mainImage]);

  const handleLightboxNext = useCallback(() => {
    if (!product || product.images.length <= 1) return;
    const currentIdx = product.images.findIndex((img) => img.imageUrl === mainImage);
    const nextIdx = currentIdx >= product.images.length - 1 ? 0 : currentIdx + 1;
    setMainImage(product.images[nextIdx].imageUrl);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  }, [product, mainImage]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') handleLightboxPrev();
      if (e.key === 'ArrowRight') handleLightboxNext();
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-') handleZoomOut();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, handleLightboxPrev, handleLightboxNext]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (zoomLevel <= 1) return;
    setIsPanning(true);
    panStartRef.current = { x: e.clientX, y: e.clientY, offsetX: panOffset.x, offsetY: panOffset.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isPanning) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    setPanOffset({ x: panStartRef.current.offsetX + dx, y: panStartRef.current.offsetY + dy });
  };

  const handlePointerUp = () => {
    setIsPanning(false);
  };

  useEffect(() => {
    storeService.getCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    const numId = Number(id);

    // Evita dupla execução em StrictMode: se já carregou este ID, pula
    if (loadedIdRef.current === numId) return;

    loadedIdRef.current = numId;
    const colorParam = searchParams.get('color');
    const varParam = searchParams.get('var');
    const colorIdParam = colorParam !== null ? Number(colorParam) : undefined;
    setLoading(true);
    storeService
      .getProduct(numId)
      .then((data) => {
        setProduct(data);
        setMainImage(data.images[0]?.imageUrl ?? '');

        const colors = extractUniqueColors(data.variants);
        let matchedColor: StoreVariantColor | undefined;

        if (colorIdParam !== undefined) {
          matchedColor = colors.find((c) => c.id === colorIdParam);
        } else if (varParam !== null) {
          const variantsWithValue = data.variants.filter((v) => v.variationValue === varParam);
          if (variantsWithValue.length > 0) {
            const variantWithStock = variantsWithValue.find((v) => v.stockQuantity > 0) ?? variantsWithValue[0];
            const variantColor = variantWithStock.color;
            if (variantColor) {
              matchedColor = colors.find((c) => c.id === variantColor.id);
            }
          }
        }

        if (matchedColor) {
          setSelectedColor(matchedColor);
        } else if (colors.length > 0) {
          const colorWithStock = colors.find((c) =>
            data.variants.some((v) => v.stockQuantity > 0 && v.color?.id === c.id)
          );
          setSelectedColor(colorWithStock ?? colors[0]);
        }
      })
      .finally(() => setLoading(false));
  }, [id, searchParams]);

  useEffect(() => {
    if (!product || !selectedColor) return;
    const colorImage = product.images.find((img) => img.colorId === selectedColor.id);
    if (colorImage) {
      setMainImage(colorImage.imageUrl);
    }
  }, [product, selectedColor]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  if (!product) return <div className="text-center py-20 text-text-light">Produto não encontrado.</div>;

  const categoryId = categories.find((c) => c.name === product.categoryName)?.id;

  const hasVariants = product.variants.length > 0;
  const hasColors = product.variants.some((v) => v.color !== null);
  const hasAgeVariation = product.variants.some((v) => v.variationTypeId === AGE_VARIATION_TYPE);

  // Unique colors across all variants
  const uniqueColors: StoreVariantColor[] = hasColors ? extractUniqueColors(product.variants) : [];

  // Unique variation values available for the selected color (all colors if none selected yet)
  const uniqueValues: string[] = [];
  {
    const seen = new Set<string>();
    for (const v of product.variants) {
      if (selectedColor && v.color?.id !== selectedColor.id) continue;
      if (!seen.has(v.variationValue)) {
        seen.add(v.variationValue);
        uniqueValues.push(v.variationValue);
      }
    }
  }

  // Resolve the actual variant from selected color + value
  const resolvedVariant: StoreVariant | null = (() => {
    if (!hasVariants) return null;
    if (hasColors && selectedColor && selectedValue) {
      return product.variants.find(
        (v) => v.variationValue === selectedValue && v.color?.id === selectedColor.id
      ) ?? null;
    }
    if (!hasColors && selectedValue) {
      return product.variants.find((v) => v.variationValue === selectedValue) ?? null;
    }
    if (hasColors && !selectedColor && uniqueColors.length === 0 && selectedValue) {
      return product.variants.find((v) => v.variationValue === selectedValue) ?? null;
    }
    return null;
  })();

  const stockQuantity = resolvedVariant?.stockQuantity ?? 0;
  const isOutOfStock = hasVariants && resolvedVariant !== null && stockQuantity === 0;
  const hasPrice = product.hasPrice && product.price !== null;
  const canAddToCart = (!hasVariants || resolvedVariant !== null) && !isOutOfStock && hasPrice;

  const handleIncreaseQuantity = () => {
    if (quantity < stockQuantity) {
      setQuantity(quantity + 1);
    }
  };

  const handleDecreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;
    if (value >= 1 && value <= stockQuantity) {
      setQuantity(value);
    }
  };

  const handleAddToCart = () => {
    if (!product || product.price === null) return;
    const result = addItem({
      productId: product.id,
      variantId: resolvedVariant?.id ?? 0,
      productName: product.name,
      variantLabel: resolvedVariant?.label ?? '',
      variantSku: resolvedVariant?.sku,
      colorName: selectedColor?.name,
      price: product.price,
      promotionalPrice: product.promotionalPrice,
      imageUrl: mainImage,
      quantity: quantity,
      stockQuantity: resolvedVariant?.stockQuantity ?? 0,
    });
    if (result === 'max_stock') return;
    setQuantity(1);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const displayPrice = product.promotionalPrice ?? product.price;
  const pixPrice =
    hasPrice && paymentSettings && !product.promotionalPrice
      ? getPixPrice(displayPrice as number, paymentSettings.pixDiscountPercentage)
      : null;
  const installmentInfo =
    hasPrice && paymentSettings && !product.promotionalPrice
      ? getInstallmentInfo(displayPrice as number, paymentSettings)
      : null;

  const handleAskOnWhatsApp = () => {
    if (!product) return;
    const variantLabel = resolvedVariant?.label ?? selectedValue ?? null;
    const url = buildProductInquiryWhatsAppUrl(product, displayPrice, selectedColor, variantLabel);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="max-w-4xl lg:max-w-5xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm font-medium text-text-medium hover:text-chocolate bg-white border border-champagne-dark hover:border-chocolate px-4 py-2 rounded-full mb-4 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Voltar
      </button>
      <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1.5 text-xs text-text-light mb-4">
        <Link to="/catalogo" className="hover:text-chocolate transition-colors">
          Catálogo
        </Link>
        <span aria-hidden="true">/</span>
        {categoryId !== undefined ? (
          <Link to={`/catalogo?cat=${categoryId}`} className="hover:text-chocolate transition-colors">
            {product.categoryName}
          </Link>
        ) : (
          <span>{product.categoryName}</span>
        )}
        <span aria-hidden="true">/</span>
        <span className="text-text-medium font-medium truncate max-w-[60vw] sm:max-w-xs">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Imagens */}
        <div className="flex flex-col gap-3 min-w-0">
          <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-champagne-light group cursor-zoom-in min-w-0 shadow-sm" onClick={openLightbox}>
            {mainImage ? (
              <img src={safeImageUrl(mainImage)} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <img src={productPlaceholderIcon(product.id)} alt="" className="w-28 h-28 opacity-70" />
              </div>
            )}
            <button
              aria-label="Ampliar imagem"
              className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 shadow-md z-10"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </button>
            {product.images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const currentIdx = product.images.findIndex((img) => img.imageUrl === mainImage);
                    const prevIdx = currentIdx <= 0 ? product.images.length - 1 : currentIdx - 1;
                    setMainImage(product.images[prevIdx].imageUrl);
                  }}
                  aria-label="Imagem anterior"
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 shadow-md z-10"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const currentIdx = product.images.findIndex((img) => img.imageUrl === mainImage);
                    const nextIdx = currentIdx >= product.images.length - 1 ? 0 : currentIdx + 1;
                    setMainImage(product.images[nextIdx].imageUrl);
                  }}
                  aria-label="Próxima imagem"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 shadow-md z-10"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {product.images.map((img) => (
                    <span
                      key={img.id}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        img.imageUrl === mainImage ? 'bg-chocolate' : 'bg-white/70'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          {product.images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {product.images.map((img) => (
                <button
                  key={img.id}
                  onClick={() => setMainImage(img.imageUrl)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                    mainImage === img.imageUrl ? 'border-chocolate' : 'border-transparent'
                  }`}
                >
                  <img src={safeImageUrl(img.imageUrl)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-chocolate-dark">
                {product.categoryName}
                {GENDER_LABELS[product.gender] && (
                  <>
                    {' · '}
                    <span className={product.gender === 1 ? 'text-olivine-dark' : undefined}>
                      {GENDER_LABELS[product.gender]}
                    </span>
                  </>
                )}
              </p>
              <h1 className="text-2xl md:text-3xl font-display font-semibold text-text-dark mt-1.5 leading-tight">{product.name}</h1>
              {product.sku && <p className="text-xs text-text-light mt-1.5 font-mono">SKU: {product.sku}</p>}
            </div>
            {!PROMOTION_CAMPAIGN_ENABLED && (
              <button
                type="button"
                onClick={(e) => handleToggle(product.id, e)}
                aria-pressed={isFavorited(product.id)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm font-medium transition-colors ${
                  isFavorited(product.id)
                    ? 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100'
                    : 'bg-white border-champagne-dark text-text-medium hover:border-chocolate hover:text-chocolate'
                }`}
              >
                <HeartIcon filled={isFavorited(product.id)} className="w-4 h-4" />
                {isFavorited(product.id) ? 'Favoritado' : 'Favoritar'}
              </button>
            )}
          </div>

          {hasPrice ? (
            <div className="flex items-baseline gap-2">
              {product.promotionalPrice && (
                <span className="text-text-light line-through text-sm">R$ {formatCurrency(product.price as number)}</span>
              )}
              <span className="text-3xl font-extrabold text-chocolate">R$ {formatCurrency(displayPrice as number)}</span>
              {product.promotionalPrice && (
                <span className="self-center bg-text-dark text-white text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-sm">
                  -{Math.round((1 - product.promotionalPrice / (product.price as number)) * 100)}%
                </span>
              )}
            </div>
          ) : (
            <p className="text-lg font-semibold text-text-medium">Consulte-nos para saber o preço</p>
          )}

          {hasPrice && pixPrice !== null && (
            <p className="text-sm text-olivine-dark font-semibold -mt-2">
              R$ {formatCurrency(pixPrice)} no Pix
            </p>
          )}
          {hasPrice && installmentInfo && (
            <p className="text-sm text-text-light -mt-2">
              ou {installmentInfo.count}x de R$ {formatCurrency(installmentInfo.amount)}
              {paymentSettings?.installmentsWithoutInterest && ' sem juros'}
            </p>
          )}

          {/* Seleção de cor */}
          {uniqueColors.length > 0 && (
            <div>
              <p className="text-sm font-medium text-text-medium mb-2">
                Cor{selectedColor && <span className="text-text-light font-medium"> — {selectedColor.name}</span>}
              </p>
              <div className="flex flex-wrap gap-2">
                {uniqueColors.map((color) => {
                  const outOfStock = !product.variants.some(
                    (v) => v.stockQuantity > 0 && v.color?.id === color.id
                  );
                  return (
                    <button
                      key={color.id}
                      onClick={() => {
                        if (outOfStock) return;
                        if (selectedColor?.id === color.id) {
                          setSelectedColor(null);
                        } else {
                          setSelectedColor(color);
                        }
                        setSelectedValue(null);
                      }}
                      disabled={outOfStock}
                      title={outOfStock ? `${color.name} (sem estoque)` : color.name}
                      aria-label={color.name}
                      aria-pressed={selectedColor?.id === color.id}
                      className={`w-9 h-9 rounded-full border-2 transition-all ${
                        outOfStock
                          ? 'opacity-50 cursor-not-allowed border-champagne-dark'
                          : selectedColor?.id === color.id
                          ? 'border-chocolate scale-110 shadow-md'
                          : 'border-champagne-dark hover:border-chocolate/60 hover:scale-105'
                      }`}
                      style={{
                        backgroundColor: color.hexadecimal,
                        backgroundImage: outOfStock
                          ? 'linear-gradient(to top right, transparent calc(50% - 1px), rgba(0,0,0,0.6) calc(50% - 1px), rgba(0,0,0,0.6) calc(50% + 1px), transparent calc(50% + 1px))'
                          : undefined,
                      }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Seleção de variação (tamanho / idade / etc) */}
          {hasVariants && uniqueValues.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-2">
                <p className="text-sm font-medium text-text-medium">{getVariationLabel(product.variants[0]?.variationTypeId)}</p>
                {hasAgeVariation && (
                  <button
                    type="button"
                    onClick={() => setSizeGuideOpen(true)}
                    className="text-xs text-chocolate underline underline-offset-2 hover:text-chocolate/80 transition-colors"
                  >
                    Ver guia de tamanhos
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {uniqueValues.map((val) => {
                  const variant = product.variants.find(
                    (v) => v.variationValue === val && (!selectedColor || v.color?.id === selectedColor.id)
                  );
                  const outOfStock = variant ? variant.stockQuantity === 0 : false;
                  return (
                    <button
                      key={val}
                      onClick={() => !outOfStock && setSelectedValue(selectedValue === val ? null : val)}
                      disabled={outOfStock}
                      title={variant?.label}
                      className={`px-3 py-1.5 rounded-lg border text-sm transition-colors relative ${
                        outOfStock
                          ? 'bg-white border-champagne-dark text-text-light opacity-50 cursor-not-allowed line-through'
                          : selectedValue === val
                          ? 'bg-chocolate text-white border-chocolate'
                          : 'bg-white border-champagne-dark text-text-medium hover:border-chocolate'
                      }`}
                    >
                      {variant?.label ?? val}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Nota de estoque */}
          {hasVariants && resolvedVariant && (
            stockQuantity === 1 ? (
              <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-danger">
                <AlertTriangleIcon className="w-3.5 h-3.5" />
                Apenas 1 unidade disponível
              </p>
            ) : stockQuantity > 1 && stockQuantity <= 3 ? (
              <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-danger">
                <AlertTriangleIcon className="w-3.5 h-3.5" />
                Últimas {stockQuantity} unidades
              </p>
            ) : (
              <p className="text-xs text-text-light">Disponível: {stockQuantity}</p>
            )
          )}

          {/* Seleção de quantidade */}
          {hasVariants && resolvedVariant && (
            <div className="inline-flex items-center border-[1.5px] border-champagne-dark rounded-full w-fit">
              <button
                onClick={handleDecreaseQuantity}
                disabled={quantity <= 1}
                aria-label="Diminuir quantidade"
                className="w-9 h-9 flex items-center justify-center text-text-dark disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <MinusIcon />
              </button>
              <input
                type="number"
                min="1"
                max={stockQuantity}
                value={quantity}
                onChange={handleQuantityChange}
                aria-label="Quantidade"
                className="w-9 text-center bg-transparent border-none text-text-dark font-bold text-sm focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <button
                onClick={handleIncreaseQuantity}
                disabled={quantity >= stockQuantity}
                aria-label="Aumentar quantidade"
                className="w-9 h-9 flex items-center justify-center text-text-dark disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <PlusIcon />
              </button>
            </div>
          )}

          {hasPrice && (
            <Button
              fullWidth
              size="lg"
              variant="primary"
              onClick={handleAddToCart}
              disabled={!canAddToCart || addedToCart}
            >
              {isOutOfStock ? (
                'Esgotado'
              ) : addedToCart ? (
                <>
                  <CheckIcon className="w-4 h-4" />
                  Adicionado
                </>
              ) : (
                'Adicionar à sacola'
              )}
            </Button>
          )}
          <button
            type="button"
            onClick={handleAskOnWhatsApp}
            className="inline-flex items-center gap-2 self-start text-text-medium hover:text-chocolate font-semibold text-sm transition-colors"
          >
            <WhatsAppIcon />
            Tirar dúvida sobre essa peça
          </button>

          <div className="border-t border-champagne-dark">
            {product.description && (
              <details className="group border-b border-champagne-dark" open>
                <summary className="flex items-center justify-between py-3 cursor-pointer select-none list-none font-semibold text-text-dark text-lg">
                  Descrição
                  <ChevronIcon />
                </summary>
                <div className="pb-4 text-base text-text-medium leading-relaxed">
                  <p>{product.description}</p>
                </div>
              </details>
            )}

            {product.compositionCare && (
              <details className="group border-b border-champagne-dark">
                <summary className="flex items-center justify-between py-3 cursor-pointer select-none list-none font-semibold text-text-dark text-lg">
                  Composição e cuidados
                  <ChevronIcon />
                </summary>
                <div className="pb-4 text-base text-text-medium leading-relaxed">
                  <p>{product.compositionCare}</p>
                </div>
              </details>
            )}

            <details className="group border-b border-champagne-dark">
              <summary className="flex items-center justify-between py-3 cursor-pointer select-none list-none font-semibold text-text-dark text-lg">
                Meios de pagamento
                <ChevronIcon />
              </summary>
              <div className="pb-4 flex flex-col gap-3">
                {[PaymentMethod.Pix, PaymentMethod.CreditCard, PaymentMethod.DebitCard, PaymentMethod.Cash].map((method) => (
                  <div key={method} className="flex items-center gap-2.5 text-base text-text-medium">
                    <span className="w-8 h-8 rounded-lg bg-champagne flex items-center justify-center text-chocolate-dark shrink-0">
                      <PaymentMethodIcon method={method} />
                    </span>
                    <span className="flex flex-col leading-tight">
                      <span className="font-semibold text-text-dark">{PaymentMethodLabel[method]}</span>
                      {method === PaymentMethod.Pix && hasPrice && pixPrice !== null && (
                        <span className="text-sm text-olivine-dark font-semibold">
                          R$ {formatCurrency(pixPrice)} à vista
                        </span>
                      )}
                      {method === PaymentMethod.CreditCard && hasPrice && installmentInfo && (
                        <span className="text-sm text-text-light">
                          em até {installmentInfo.count}x de R$ {formatCurrency(installmentInfo.amount)}
                          {paymentSettings?.installmentsWithoutInterest && ' sem juros'}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </details>

            <details className="group border-b border-champagne-dark">
              <summary className="flex items-center justify-between py-3 cursor-pointer select-none list-none font-semibold text-text-dark text-lg">
                Entrega
                <ChevronIcon />
              </summary>
              <div className="pb-4 text-base text-text-medium leading-relaxed">
                <p>
                  Enviamos para todo o Brasil. O valor e o prazo variam de acordo com a sua região — combinamos
                  o frete diretamente pelo WhatsApp depois que você finaliza o pedido.
                </p>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 font-semibold text-chocolate hover:text-chocolate-dark transition-colors mt-2"
                >
                  Falar no WhatsApp
                </a>
              </div>
            </details>

            <details className="group border-b border-champagne-dark">
              <summary className="flex items-center justify-between py-3 cursor-pointer select-none list-none font-semibold text-text-dark text-lg">
                Trocas e devoluções
                <ChevronIcon />
              </summary>
              <div className="pb-4 text-base text-text-medium leading-relaxed">
                <ol className="space-y-2 list-decimal list-inside">
                  <li>As peças devem estar sem sinais de uso, lavagem ou alteração, com etiqueta e lacre originais intactos.</li>
                  <li>Não realizamos trocas de peças promocionais ou adquiridas em liquidação.</li>
                  <li>Não realizamos trocas de peças de coleções sazonais.</li>
                  <li>Prazo para a troca: 7 dias corridos.</li>
                  <li>A troca da peça pode ser realizada uma vez.</li>
                  <li>Não trocamos peças íntimas.</li>
                  <li>A troca será permitida apenas por outro modelo da mesma coleção ou por tamanho diferente da mesma peça, conforme disponibilidade em estoque.</li>
                </ol>
              </div>
            </details>
          </div>
        </div>
      </div>

      <ProductReviews productId={product.id} />

      {/* Lightbox */}
      {lightboxOpen && mainImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Toolbar */}
          <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
            <span className="text-white/60 text-sm mr-2">{Math.round(zoomLevel * 100)}%</span>
            <button
              onClick={(e) => { e.stopPropagation(); handleZoomOut(); }}
              disabled={zoomLevel <= 1}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
              </svg>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleZoomIn(); }}
              disabled={zoomLevel >= 4}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </button>
            <button
              onClick={closeLightbox}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Prev/Next */}
          {product.images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); handleLightboxPrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleLightboxNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Image */}
          <div
            className="max-w-[90vw] max-h-[85vh] overflow-hidden select-none"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{ cursor: zoomLevel > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default' }}
          >
            <img
              src={safeImageUrl(mainImage)}
              alt={product.name}
              className="max-w-[90vw] max-h-[85vh] object-contain transition-transform duration-200"
              style={{
                transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
              }}
              draggable={false}
            />
          </div>

          {/* Thumbnails */}
          {product.images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {product.images.map((img) => (
                <button
                  key={img.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMainImage(img.imageUrl);
                    setZoomLevel(1);
                    setPanOffset({ x: 0, y: 0 });
                  }}
                  className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-colors ${
                    mainImage === img.imageUrl ? 'border-white' : 'border-white/30 hover:border-white/60'
                  }`}
                >
                  <img src={safeImageUrl(img.imageUrl)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal open={sizeGuideOpen} onClose={() => setSizeGuideOpen(false)} title="Guia de tamanhos">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr className="border-b border-champagne-dark">
                <th className="py-2 pr-3 font-medium text-text-medium">Idade</th>
                <th className="py-2 pr-3 font-medium text-text-medium">Altura aproximada</th>
                <th className="py-2 font-medium text-text-medium">Peso aproximado</th>
              </tr>
            </thead>
            <tbody>
              {AGE_SIZE_GUIDE.map((row) => (
                <tr key={row.age} className="border-b border-champagne">
                  <td className="py-2 pr-3 text-text-dark font-medium whitespace-nowrap">{row.age}</td>
                  <td className="py-2 pr-3 text-text-medium whitespace-nowrap">{row.height}</td>
                  <td className="py-2 text-text-medium whitespace-nowrap">{row.weight}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-text-light mt-4">
          Medidas aproximadas — podem variar conforme o modelo da peça. Em caso de dúvida, fale com a vendedora.
        </p>
      </Modal>
    </div>
  );
}
